/**
 * 各会議タブの冒頭に置く “前回会議のふりかえり” と、会議タブの一覧。
 * 週次会議はこのタブを見ながら進める前提なので、その会議の前回分を最初に確認できるようにする。
 *
 * タブの表示名・どの会議がどのタブかの紐づけ・前回分の中身は deploy 供給
 * （/api/meeting-board/tabs, /previous）が持つ。ここは並べ方だけを担当する。
 */
import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, History } from 'lucide-react'

import { Badge } from '@/components/Badge'
import { Card } from '@/components/Card'
import { useBoardSection, useBoardTabs } from '@/hooks/useMeetingBoard'
import { cn } from '@/lib/utils'

interface PrevItem {
  id: string
  kind: 'action' | 'decision' | 'issue' | 'agenda'
  summary: string
  /** 個人の事情にあたる記述をAPI側で落としてある */
  redacted?: boolean
  owner: string | null
  dueAt: string | null
  confidence: number
  reviewStatus: string
  createdAt: string
  runId: string
}

/**
 * そのタブを持っている人。
 *
 * `memberRef` は利用者の安定 ID。将来「このタブを直せるのは誰か」を判定する鍵になる。
 * 利用者がまだ登録されていない場合は null で、名前だけが出る。
 * 名前で判定はしない（同姓・改名・退職で壊れるため）。
 */
export interface MeetingTabOwner {
  memberRef: string | null
  displayName: string
}

export interface MeetingTab {
  key: string
  label: string | null
  isExtraTab: boolean
  /** 未指定・空配列は「担当が決まっていない」 */
  owners?: MeetingTabOwner[]
  /** 専用パネルで別に描画するタブ。取組一覧に二重で出さないための印。 */
  renderedElsewhere?: boolean
  sourceState: 'linked' | 'no_content' | 'unset'
  sourceNote: string | null
  requiresReview: boolean
  meetingTopic: string | null
  meetingStart: string | null
  counts: { decision: number; action: number; issue: number }
  /** 毎朝の日報がその取組にあるか。未配信ならその理由 */
  dailyReportState: 'delivered' | 'not_delivered' | null
  dailyReportNote: string | null
}

interface PrevMeeting {
  tab: string
  label: string | null
  sourceNote: string | null
  requiresReview: boolean
  meetingTopic: string | null
  meetingStart: string | null
  items: PrevItem[]
  total: number
}

/** 折りたたみ時に各カテゴリで見せる件数 */
const COLLAPSED = 4

const GROUPS: { kind: PrevItem['kind']; title: string; short: string; tone: 'success' | 'warning' | 'destructive'; empty: string }[] = [
  { kind: 'decision', title: '決めたこと', short: '決めた', tone: 'success', empty: '前回の決定は抽出されていません' },
  { kind: 'action', title: '宿題になったこと', short: '宿題', tone: 'warning', empty: '前回の宿題は抽出されていません' },
  { kind: 'issue', title: '課題として挙がったこと', short: '課題', tone: 'destructive', empty: '前回の課題は抽出されていません' },
]

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土']

function formatMeetingStart(iso: string): string {
  const d = new Date(iso)
  const parts = new Intl.DateTimeFormat('ja-JP', {
    month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
    weekday: 'short', timeZone: 'Asia/Tokyo',
  }).formatToParts(d)
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? ''
  return `${get('month')}/${get('day')}(${get('weekday') || WEEKDAYS[d.getDay()]}) ${get('hour')}:${get('minute')}`
}

export function useMeetingTabs(): MeetingTab[] {
  return useBoardTabs<{ items: MeetingTab[] }>().data?.items ?? []
}

/** そのタブの担当。タブ定義が未取得・未配置なら空。 */
export function useTabOwners(tab: string): MeetingTabOwner[] {
  return useMeetingTabs().find((item) => item.key === tab)?.owners ?? []
}

/** タブ定義がまだ取得中か。取得前に既定タブを確定させないために使う。 */
export function useMeetingTabsLoading(): boolean {
  return useBoardTabs<{ items: MeetingTab[] }>().isLoading
}

function usePreviousMeeting(tab: string) {
  return useBoardSection<PrevMeeting>('previous', tab)
}

// 「前回動いた人」は本文からの人名推測に依存していたため廃止（2026-08-09 沖中判断）。
// 「工事日」のような人名でない語を担当者として出す誤りが実際に出たため、
// 担当は元データに入っている値だけを表示する。

export function BoardPreviousMeetingSection({ tab }: { tab: string }) {
  const [expanded, setExpanded] = useState(false)
  const query = usePreviousMeeting(tab)

  const grouped = useMemo(
    () => GROUPS.map((group) => ({
      ...group,
      items: (query.data?.items ?? [])
        .filter((item) => item.kind === group.kind)
        // 確信度が同じものが多いので、同点なら具体的に書けている（＝長い）ものを先に出す
        .sort((a, b) => b.confidence - a.confidence || b.summary.length - a.summary.length),
    })),
    [query.data],
  )

  if (query.isLoading) {
    return (
      <Card className="p-4">
        <p className="text-sm text-muted-foreground">前回会議を読み込んでいます…</p>
      </Card>
    )
  }
  if (query.isError || !query.data) {
    return (
      <Card className="p-4">
        <p className="text-sm text-yellow-600">前回会議の内容を取得できませんでした</p>
      </Card>
    )
  }

  const { meetingTopic, meetingStart, sourceNote, requiresReview, items } = query.data

  if (items.length === 0) {
    return (
      <Card className="px-4 py-4 md:px-6">
        <h2 className="font-semibold">前回会議のふりかえり</h2>
        <p className="mt-1 text-sm text-yellow-600">
          {sourceNote ?? 'この会議の内容はまだ取り込まれていません'}
        </p>
      </Card>
    )
  }

  const hidden = grouped.reduce((sum, g) => sum + Math.max(0, g.items.length - COLLAPSED), 0)

  return (
    <Card>
      <div className="flex flex-col gap-2 border-b px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          <div>
            <h2 className="font-semibold">前回会議のふりかえり</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {meetingStart && <span className="tabular-nums">{formatMeetingStart(meetingStart)}</span>}
              {meetingTopic && <span className="ml-2">{meetingTopic}</span>}
              <span className="ml-2">抽出 {items.length}件</span>
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {requiresReview && <Badge tone="warning" className="rounded-md">会議の紐づけ要確認</Badge>}
          {grouped.map((group) => (
            <Badge key={group.kind} tone={group.tone} className="rounded-md tabular-nums">
              {group.short} {group.items.length}
            </Badge>
          ))}
        </div>
      </div>

      <div className="grid gap-6 p-4 md:grid-cols-3 md:p-6">
        {grouped.map((group) => {
          const shown = expanded ? group.items : group.items.slice(0, COLLAPSED)
          return (
            <section key={group.kind}>
              <h3 className="text-sm font-medium">{group.title}</h3>
              {shown.length === 0 ? (
                <p className="mt-2 text-sm text-yellow-600">{group.empty}</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {shown.map((item) => (
                    <li key={item.id} className="text-sm leading-relaxed">
                      <span
                        className={cn(
                          'mr-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full align-middle',
                          group.tone === 'success' ? 'bg-green-600'
                            : group.tone === 'warning' ? 'bg-yellow-600' : 'bg-destructive',
                        )}
                        aria-hidden
                      />
                      {item.summary}
                      {item.redacted && (
                        <span className="ml-2 whitespace-nowrap text-xs text-muted-foreground">（個人に関する記述を伏せています）</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {!expanded && group.items.length > COLLAPSED && (
                <p className="mt-2 text-xs tabular-nums text-muted-foreground">ほか {group.items.length - COLLAPSED}件</p>
              )}
            </section>
          )
        })}
      </div>

      <div className="flex flex-col gap-2 border-t px-4 py-3 text-xs text-muted-foreground md:flex-row md:items-center md:justify-between md:px-6">
        <p>会議の録画から自動で抜き出した内容です。承認前のため、会議の場で内容を確認してください。</p>
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex min-h-9 shrink-0 items-center gap-1 rounded-md border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-expanded={expanded}
          >
            {expanded ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
            {expanded ? '主なものだけ表示' : `すべて表示（あと${hidden}件）`}
          </button>
        )}
      </div>
    </Card>
  )
}
